import { useEffect, useState, useCallback } from "react";
import { Radio, Plus, Trash2, Loader2, Save, Edit, X, Bell, Users } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl, uploadImage, formatApiErrorDetail } from "../lib/api";

function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const EMPTY = { title: "", host: "TSU-Team", description: "", starts_at: "", duration_minutes: 60, location: "", link: "", image_url: "" };

function Form({ initial, onSave, onCancel, busy }) {
  const [form, setForm] = useState(initial ? {
    title: initial.title || "",
    host: initial.host || "TSU-Team",
    description: initial.description || "",
    starts_at: isoToLocalInput(initial.starts_at),
    duration_minutes: initial.duration_minutes || 60,
    location: initial.location || "",
    link: initial.link || "",
    image_url: initial.image_url || "",
  } : EMPTY);
  const [uploading, setUploading] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const up = await uploadImage(f);
      setForm((p) => ({ ...p, image_url: up.url }));
      toast.success("Bild hochgeladen.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload fehlgeschlagen.");
    } finally {
      setUploading(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.title || !form.starts_at) { toast.error("Titel und Datum benötigt."); return; }
    onSave({
      title: form.title.trim(),
      host: form.host.trim() || "TSU-Team",
      description: form.description.trim() || null,
      starts_at: new Date(form.starts_at).toISOString(),
      duration_minutes: parseInt(form.duration_minutes, 10) || 60,
      location: form.location.trim() || null,
      link: form.link.trim() || null,
      image_url: form.image_url || null,
    });
  };

  return (
    <form onSubmit={submit} className="bg-[#FFFFFF] border border-[#1D5BFF]/30 rounded-2xl p-5 space-y-3" data-testid="live-form">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2">
          <Radio className="w-5 h-5 text-red-400" /> {initial ? "Live-Session bearbeiten" : "Neue Live-Session"}
        </h3>
        <button type="button" onClick={onCancel} className="p-2 hover:bg-slate-100/70 rounded-full"><X className="w-4 h-4 text-slate-600" /></button>
      </div>
      <Field label="Titel *"><input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} placeholder="Live Talk & Q&A" data-testid="live-title" className="input" maxLength={160} /></Field>
      <Field label="Moderator"><input value={form.host} onChange={(e) => setForm((p) => ({ ...p, host: e.target.value }))} placeholder="TSU-Team" data-testid="live-host" className="input" maxLength={80} /></Field>
      <Field label="Beschreibung"><textarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} rows={3} className="input resize-y" maxLength={2000} placeholder="Worum geht es? Welche Themen?" /></Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Startzeit *"><input type="datetime-local" value={form.starts_at} onChange={(e) => setForm((p) => ({ ...p, starts_at: e.target.value }))} data-testid="live-starts" className="input" /></Field>
        <Field label="Dauer (Min)"><input type="number" min="5" max="480" value={form.duration_minutes} onChange={(e) => setForm((p) => ({ ...p, duration_minutes: e.target.value }))} className="input" /></Field>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Ort/Plattform"><input value={form.location} onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))} placeholder="z. B. WhatsApp Gruppenanruf, Zoom, Discord" className="input" maxLength={200} /></Field>
        <Field label="Link"><input value={form.link} onChange={(e) => setForm((p) => ({ ...p, link: e.target.value }))} placeholder="https://…" className="input" maxLength={500} /></Field>
      </div>
      <Field label="Bild (optional)">
        <div className="flex items-center gap-3">
          {form.image_url && <img src={fileUrl(form.image_url)} alt="" className="w-16 h-16 rounded-lg object-cover border border-slate-200" />}
          <label className="cursor-pointer bg-[#F5F8FD] border border-slate-200 hover:border-[#1D5BFF]/50 rounded-xl px-3 py-2 text-xs text-slate-700">
            {uploading ? "Lädt…" : (form.image_url ? "Ändern" : "Hochladen")}
            <input type="file" accept="image/*" onChange={onFile} className="hidden" />
          </label>
          {form.image_url && <button type="button" onClick={() => setForm((p) => ({ ...p, image_url: "" }))} className="text-xs text-red-400">entfernen</button>}
        </div>
      </Field>
      <div className="flex gap-2 pt-2">
        <button type="submit" disabled={busy || uploading} data-testid="save-live-btn" className="bg-[#1D5BFF] hover:bg-[#1546C9] disabled:opacity-50 text-white font-semibold px-5 py-2.5 rounded-full flex items-center gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {initial ? "Speichern" : "Session anlegen"}
        </button>
        <button type="button" onClick={onCancel} className="bg-slate-100/70 hover:bg-slate-100 text-slate-700 font-semibold px-5 py-2.5 rounded-full">Abbrechen</button>
      </div>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-slate-600 block mb-1">{label}</span>
      {children}
    </label>
  );
}

export default function AdminLiveSessions() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/live-sessions"); setList(r.data); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (payload) => {
    setBusy(true);
    try {
      if (editing) { await api.patch(`/admin/live-sessions/${editing.id}`, payload); toast.success("Aktualisiert."); }
      else { await api.post("/admin/live-sessions", payload); toast.success("Session angelegt 🎙️"); }
      setShowForm(false); setEditing(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler"); }
    finally { setBusy(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Session "${s.title}" wirklich löschen?`)) return;
    try { await api.delete(`/admin/live-sessions/${s.id}`); toast.success("Gelöscht."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler"); }
  };

  return (
    <div data-testid="admin-live-sessions" className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs sm:text-sm text-slate-600 flex-1 min-w-0">Plane Live Talks & Q&A. Mitglieder können Erinnerungen aktivieren.</div>
        {!showForm && (
          <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="new-live-btn" className="bg-[#1D5BFF] hover:bg-[#1546C9] text-white font-semibold px-4 py-2 rounded-full flex items-center gap-2 shrink-0 text-sm">
            <Plus className="w-4 h-4" /> Neue Session
          </button>
        )}
      </div>

      {showForm && <Form initial={editing} onSave={save} onCancel={() => { setShowForm(false); setEditing(null); }} busy={busy} />}

      {loading ? (
        <div className="flex items-center justify-center py-6 text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Lade…</div>
      ) : list.length === 0 ? (
        <div className="bg-[#FFFFFF] border border-slate-200/60 rounded-2xl p-10 text-center text-slate-500 text-sm">Noch keine Live-Sessions geplant.</div>
      ) : (
        <div className="space-y-2">
          {list.map((s) => (
            <div key={s.id} className="bg-[#FFFFFF] border border-slate-200 rounded-xl p-4 flex items-start gap-3 flex-wrap" data-testid={`admin-live-${s.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-bold text-slate-900 truncate">{s.title}</h4>
                  <Pill status={s.status} />
                </div>
                <div className="text-xs text-slate-600 mt-1">mit {s.host} · {new Date(s.starts_at).toLocaleString("de-DE")} · {s.duration_minutes} Min</div>
                {s.location && <div className="text-xs text-slate-500 mt-0.5">📍 {s.location}</div>}
                <div className="text-xs text-slate-500 mt-1 flex items-center gap-3">
                  <span className="flex items-center gap-1"><Bell className="w-3 h-3" /> {s.reminders_count} Erinnerungen</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 shrink-0">
                <button onClick={() => { setEditing(s); setShowForm(true); }} data-testid={`edit-live-${s.id}`} className="px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-100/70 hover:bg-slate-100 text-slate-700 flex items-center gap-1"><Edit className="w-3.5 h-3.5" /> Bearbeiten</button>
                <button onClick={() => remove(s)} data-testid={`delete-live-${s.id}`} className="px-3 py-1.5 rounded-full text-xs font-semibold bg-red-500/15 hover:bg-red-500 hover:text-slate-900 text-red-300 border border-red-500/30 flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Löschen</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <style>{`.input{width:100%;background:#F5F8FD;border:1px solid rgba(255,255,255,0.1);border-radius:0.75rem;padding:0.625rem 0.75rem;color:#fff;outline:none}.input:focus{border-color:#1D5BFF}`}</style>
    </div>
  );
}

function Pill({ status }) {
  if (status === "live") return <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 animate-pulse">LIVE</span>;
  if (status === "ended") return <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-500/20 text-slate-600">Beendet</span>;
  return <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#1D5BFF]/20 text-[#4DBFFF]">Bevorstehend</span>;
}
